import {useEffect, useMemo, useState} from "react"
import {createUseStyles} from 'react-jss'
import ConfirmModal from "../../components/ConfirmModal"
import FullScreenLoadingIndicator from "../../components/FullScreenLoadingIndicator"
import {CompanyGroupPortalRoleModel, CreateOrUpdateCompanyGroupPortalRoleModel} from "../../redux/types"
import Button from '../../components/Button'
import {useAppSelector} from "../../redux/hooks"
import Card from "../../components/Card"
import {useTranslation} from "react-i18next"
import useCompanyGroupPortalUserActions from "../../hooks/useCompanyGroupPortalUserActions"
import useCompanyGroupPortalRolesActions from "../../hooks/useCompanyGroupPortalRolesActions"
import CompanyGroupPortalRoleCard from "../../components/PortalRoles/CompanyGroupPortalRoleCard"
import EditCompanyGroupPortalRoleModal from "../../components/PortalRoles/EditCompanyGroupPortalRoleModal"

const CompanyGroupPortalRoles = () => {
  const styles = useStyles()
  const {t} = useTranslation('portalRoles');

  const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState<boolean>(false)
  const [deleteCandidate, setDeleteCandidate] = useState<CompanyGroupPortalRoleModel>()

  const [editModalVisible, setEditModalVisible] = useState<boolean>(false)
  const [editPortalRoleModel, setEditPortalRoleModel] = useState<CompanyGroupPortalRoleModel>()

  const {getUsers} = useCompanyGroupPortalUserActions()
  const {getRoles, createRole, updateRole, deleteRole} = useCompanyGroupPortalRolesActions()

  const usersLoading = useAppSelector(state => state.companyGroupPortalUser.loading)
  const portalUsers = useAppSelector(state => state.companyGroupPortalUser.portalUsers)
  const portalRoles = useAppSelector(state => state.companyGroupPortalRoles.portalRoles)
  const rolesLoading = useAppSelector(state => state.companyGroupPortalRoles.loading)
  
  const sortedPortalRoles = useMemo(() => [...portalRoles].sort((a, b) => a.name.localeCompare(b.name)), [portalRoles])

  useEffect(() => {
    if (portalUsers.length === 0) {
      getUsers()
    }
  }, [])

  useEffect(() => {
    if (portalRoles.length === 0) {
      getRoles()
    }
  }, []);

  const onAddClick = () => {
    setEditPortalRoleModel(undefined)
    setEditModalVisible(true)
  }

  const onEditClick = (portalRole: CompanyGroupPortalRoleModel) => {
    setEditPortalRoleModel(portalRole)
    setEditModalVisible(true)
  }

  const handlePortalRoleEdited = (createOrUpdatePortalRoleModel: CreateOrUpdateCompanyGroupPortalRoleModel) => {
    if (editPortalRoleModel) {
      updateRole(editPortalRoleModel.id, createOrUpdatePortalRoleModel)
      setEditPortalRoleModel(undefined)
      setEditModalVisible(false)
    }
    else {
      createRole(createOrUpdatePortalRoleModel)
      setEditModalVisible(false)
    }
  }

  const handlePortalRoleEditCancelled = () => {
    setEditPortalRoleModel(undefined)
    setEditModalVisible(false)
  }

  const onDeleteClick = (portalRole: CompanyGroupPortalRoleModel) => {
    setDeleteCandidate(portalRole)
    setConfirmDeleteModalVisible(true)
  }

  const handleDeleteConfirmed = () => {
    if (deleteCandidate) {
      deleteRole(deleteCandidate.id)
      setDeleteCandidate(undefined)
    }
    setConfirmDeleteModalVisible(false)
  }

  return (
    <div className={styles.container}>
      <Card>
        <h2>
          {t('title')}
        </h2>
      </Card>

      <Card className={styles.buttonCard}>
        <Button onClick={onAddClick} title={t('addPortalRole')}/>
      </Card>

      {sortedPortalRoles.map(role =>
        <CompanyGroupPortalRoleCard
          key={role.id}
          companyGroupPortalRole={role}
          companyGroupPortalUsers={portalUsers}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
        />
      )}

      <FullScreenLoadingIndicator visible={usersLoading || rolesLoading}/>

      <EditCompanyGroupPortalRoleModal
        portalRole={editPortalRoleModel}
        visible={editModalVisible}
        onConfirmClick={handlePortalRoleEdited}
        onCancelClick={handlePortalRoleEditCancelled}
      />

      <ConfirmModal
        visible={confirmDeleteModalVisible}
        confirmText={t('confirmDeletePortalRole', {ns: 'portalRoles'})}
        onConfirmClick={handleDeleteConfirmed}
        onCancelClick={() => setConfirmDeleteModalVisible(false)}
      />
    </div>
  )
}

export default CompanyGroupPortalRoles

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  buttonCard: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  }
})
